import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SelectionTileComponent } from '@woolworthsnz/form';
import { AppSettingsService, FeatureService } from '@woolworthsnz/styleguide';
import {
	ContextResponse,
	ExpressFulfilmentAvailabilityStatusResponse,
	FulfilmentWindowSummarySlot,
	OrderViewModel,
	ShellResponse,
} from '@woolworthsnz/trader-api';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { distinctUntilChanged } from 'rxjs/operators';
import {
	getExpressFulfilmentMethodLabel,
	getExpressSlotLabel,
	getMinimumTimeForExpressOrdersToBeReady,
} from '../../helpers';
import { FulfilmentStoreService } from '../../services';
import AvailabilityStatusEnum = ExpressFulfilmentAvailabilityStatusResponse.AvailabilityStatusEnum;
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

dayjs.extend(customParseFormat);

@Component({
	selector: 'fulfilment-express-slot-selection',
	templateUrl: './fulfilment-express-slot-selection.component.html',
	styleUrls: ['./fulfilment-express-slot-selection.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [FormsModule, ReactiveFormsModule, SelectionTileComponent],
})
export class FulfilmentExpressSlotSelectionComponent implements OnInit {
	@Input() slot?: FulfilmentWindowSummarySlot;
	@Input() controlName: string;
	@Input() date: string;
	@Input() formGroup: FormGroup;
	@Input() expressSlotStatus: AvailabilityStatusEnum;

	isRebrandToDeliveryNowEnabled = false;
	featureServiceState$ = this.featureService.state$.pipe(takeUntilDestroyed());
	appSettingsServiceState$ = this.appSettingsService.state$.pipe(takeUntilDestroyed());
	fulfilmentStoreServiceMethod$ = this.fulfilmentStoreService
		.select('method')
		.pipe(takeUntilDestroyed(), distinctUntilChanged());

	fulfilmentMethod?: OrderViewModel.DeliveryMethodEnum;

	get fulfilmentMethodLabel(): string {
		return getExpressFulfilmentMethodLabel(this.fulfilmentMethod);
	}

	get isDisabled(): boolean {
		return (
			this.expressSlotStatus !== AvailabilityStatusEnum.Available &&
			this.expressSlotStatus !== AvailabilityStatusEnum.ExpressWindowAlreadySelected
		);
	}

	get label(): string {
		return getExpressSlotLabel(
			this.isDisabled,
			this.isRebrandToDeliveryNowEnabled,
			this.expressFulfilmentSettings,
			this.fulfilmentMethod
		);
	}

	get description(): string {
		if (
			this.expressSlotStatus === AvailabilityStatusEnum.NoExpressWindows ||
			this.expressSlotStatus === AvailabilityStatusEnum.WindowLimitExceeded
		) {
			return 'Our team is currently busy';
		}

		const expressFulfilmentTime = getMinimumTimeForExpressOrdersToBeReady(
			this.fulfilmentMethod,
			this.expressFulfilmentSettings
		);
		const orderReadyTime = dayjs(new Date()).add(expressFulfilmentTime, 'minute').format('h:mm a');

		return `Order now for ${orderReadyTime} ${this.fulfilmentMethodLabel.toLowerCase()}`;
	}

	expressFulfilmentSettings: ShellResponse['expressFulfilmentSettings'];

	constructor(
		private cdr: ChangeDetectorRef,
		private featureService: FeatureService,
		private appSettingsService: AppSettingsService,
		private fulfilmentStoreService: FulfilmentStoreService
	) {}

	ngOnInit(): void {
		this.appSettingsServiceState$.subscribe((appSettingsState) => {
			this.expressFulfilmentSettings = appSettingsState.expressFulfilmentSettings;
		});

		this.fulfilmentStoreServiceMethod$.subscribe((method) => {
			if (method) {
				this.fulfilmentMethod = method;
			}
			this.cdr.markForCheck();
		});

		this.featureServiceState$.subscribe(() => {
			this.isRebrandToDeliveryNowEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.EnableRebrandToDeliveryNow
			);

			this.cdr.markForCheck();
		});
	}
}
